import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";

export const ApprovalStats = [
  {
    title: "Pending Approval",
    objKeyRepr: "pending",
    icon: <HourglassEmptyOutlinedIcon fontSize="small" />,
  },
  {
    title: "Approved",
    objKeyRepr: "approved",
    icon: <CheckCircleOutlineIcon fontSize="small" />,
  },
  {
    title: "Rejected",
    objKeyRepr: "rejected",
    icon: <BlockOutlinedIcon fontSize="small" />,
  },
  {
    title: "Total Approval",
    objKeyRepr: "total",
    icon: <CheckCircleOutlineIcon fontSize="small" />,
  },
];

export const APPROVAL_STATUS_OPTIONS = [
  { label: "Pending", value: "PENDING" },
  { label: "Approved", value: "APPROVED" },
  { label: "Rejected", value: "REJECTED" },
];

const APPROVAL_STATUS_META = {
  PENDING: { label: "Pending", color: "#92400e", bg: "#fffbeb" },
  APPROVED: { label: "Approved", color: "#065f46", bg: "#ecfdf5" },
  REJECTED: { label: "Rejected", color: "#991b1b", bg: "#fef2f2" },
};

export const getApprovalStatusMeta = (status) =>
  APPROVAL_STATUS_META[status] ?? {
    label: status ?? "N/A",
    color: "text.secondary",
    bg: "rgba(0,0,0,0.06)",
  };

// module/action/target values from the API read like "report",
// "report_config_updated" or camelCase field names ("createdByName") -
// normalize all of those into a readable label.
export const formatApprovalLabel = (value) =>
  value
    ? String(value)
        .replace(/_/g, " ")
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/\b\w/g, (c) => c.toUpperCase())
    : "N/A";

// Approving/rejecting an item here also changes that item's own resource
// (e.g. a report's status), which the owning module's list/stats hooks have
// already cached under their own query keys. Maps an approval-queue `module`
// value to those query keys so acting on an approval also refreshes the
// owning module's views. Only "report" is wired up for now - add more
// modules here as they get their own approval queue integration.
const MODULE_QUERY_KEYS = {
  REPORT: [["reports"], ["reportStats"]],
};

export const getModuleQueryKeys = (module) =>
  MODULE_QUERY_KEYS[String(module).toUpperCase()] ?? [];



export const CLOSURE_ALLOWED_ROLES = [
  "ROLE_ADMIN_OWNER",
  "ROLE_ADMIN_SUPER",
  "ROLE_ADMIN_APP",
  "ROLE_SUPER_ADMIN", // spec-doc spelling; harmless to accept both (see ledger/data.js)
];

// Request lifecycle. The three extra states past the headline filters come
// from the spec's state table; confirm the exact strings with backend.
export const CLOSURE_STATUS_OPTIONS = [
  { label: "Pending Review", value: "PENDING_REVIEW" },
  { label: "Approved", value: "APPROVED" },
  { label: "Rejected", value: "REJECTED" },
  { label: "Awaiting Merchant", value: "AWAITING_MERCHANT_CONFIRMATION" },
  { label: "Withdrawn", value: "WITHDRAWN" },
];

export const CLOSURE_BLOCKED_OPTIONS = [
  { label: "Blocked only", value: "true" },
  { label: "Not blocked", value: "false" },
];

export const CLOSURE_SORT_OPTIONS = [
  { label: "Newest first", value: "desc" },
  { label: "Oldest first", value: "asc" },
];

export const CLOSURE_STATUS_STYLES = {
  PENDING_REVIEW: { bg: "#eff6ff", color: "#1e40af", dot: "#3b82f6" },
  APPROVED: { bg: "#ecfdf5", color: "#065f46", dot: "#10b981" },
  REJECTED: { bg: "#fef2f2", color: "#991b1b", dot: "#ef4444" },
  AWAITING_MERCHANT_CONFIRMATION: { bg: "#fffbeb", color: "#92400e", dot: "#f59e0b" },
  WITHDRAWN: { bg: "#f3f4f6", color: "#374151", dot: "#9ca3af" },
};

// Account state is separate from request status — an account sits in ACTIVE
// for the whole time its request is PENDING_REVIEW.
export const ACCOUNT_STATE_STYLES = {
  ACTIVE: { bg: "#ecfdf5", color: "#065f46", dot: "#10b981" },
  DEACTIVATED: { bg: "#fffbeb", color: "#92400e", dot: "#f59e0b" },
  ARCHIVED: { bg: "#fef2f2", color: "#991b1b", dot: "#ef4444" },
  LOCKED: { bg: "#f3f4f6", color: "#374151", dot: "#6b7280" },
  AVAILABLE: { bg: "#eff6ff", color: "#1e40af", dot: "#3b82f6" },
};

// Deep links for the pending-actions panel. ⚠️ Only the dispute and settlement
// list routes are confirmed to exist; there is no single-dispute detail route
// in the app today (the sheet is the only view), so these land on the list.
// An action type with no entry renders without a link rather than a dead one.
export const PENDING_ACTION_LINKS = {
  OPEN_DISPUTE: () => "/disputes/chargebacks",
  AWAITING_SETTLEMENT: () => "/settlements",
  AWAITING_REFUND: () => null,
  KYC_PENDING: (action) =>
    action?.referenceId ? `/kyc/${action.referenceId}` : "/kyc",
};

export const PENDING_ACTION_LABELS = {
  OPEN_DISPUTE: "Open dispute",
  AWAITING_SETTLEMENT: "Awaiting settlement",
  AWAITING_REFUND: "Awaiting refund",
  KYC_PENDING: "KYC pending",
};
